import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserPlus, Music, Video, Sparkles, Flag, Activity, Search, Trash2, UserMinus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { api } from '@/lib/api';

interface Event {
  // Os SETE que `GET /admin/activity-feed` devolve
  // (`admin.controller.js:772`). Declarava cinco até 08/10/2026, e os dois que
  // faltavam caíam no fallback — que imprimia o CÓDIGO CRU na tela, onde todo
  // o resto tem frase. Era o que o Pablo viu no painel.
  type:
    | 'user_signup'
    | 'track_published'
    | 'clip_published'
    | 'pro_subscription'
    | 'content_report'
    | 'content_deleted'
    | 'account_closed'
    | 'account_deleted_by_admin';
  ref_id: string;
  at: string;
  actor_username: string;
  actor_name: string;
  title: string | null;
}

const TYPE_META: Record<Event['type'], { icon: LucideIcon; label: string; verb: string; iconClass: string }> = {
  user_signup:      { icon: UserPlus,  label: 'Cadastro',  verb: 'criou conta',                iconClass: 'text-emerald-400' },
  track_published:  { icon: Music,     label: 'Música',    verb: 'publicou a música',           iconClass: 'text-brand' },
  clip_published:   { icon: Video,     label: 'Clipe',     verb: 'publicou o clipe',            iconClass: 'text-brand' },
  pro_subscription: { icon: Sparkles,  label: 'Pro',       verb: 'virou Pro com o plano',       iconClass: 'text-amber-400' },
  content_report:   { icon: Flag,      label: 'Denúncia',  verb: 'denunciou conteúdo —',        iconClass: 'text-red-400' },
  content_deleted:  { icon: Trash2,    label: 'Remoção',   verb: 'removeu',                     iconClass: 'text-orange-400' },
  // Encerrar a própria conta e ser excluído pelo painel NÃO são a mesma coisa:
  // no primeiro a linha fica, anonimizada; no segundo ela some e o conteúdo vai
  // por cascata. Até 08/10/2026 os dois vinham como um tipo só, e o código cru
  // da ação aparecia no texto do evento.
  account_closed:   { icon: UserMinus, label: 'Conta encerrada', verb: 'encerrou a conta, removendo', iconClass: 'text-orange-400' },
  account_deleted_by_admin:
                    { icon: Trash2,    label: 'Conta excluída',  verb: 'excluiu pelo painel a conta de', iconClass: 'text-red-400' },
};

const TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '',                 label: 'Todos os tipos' },
  { value: 'user_signup',      label: 'Cadastros' },
  { value: 'track_published',  label: 'Músicas' },
  { value: 'clip_published',   label: 'Clipes' },
  { value: 'pro_subscription', label: 'Assinaturas Pro' },
  { value: 'content_report',   label: 'Denúncias' },
  { value: 'content_deleted',  label: 'Remoções' },
  { value: 'account_closed',           label: 'Contas encerradas pela pessoa' },
  { value: 'account_deleted_by_admin', label: 'Contas excluídas pelo painel' },
];

const SINCE_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Todo período' },
  { value: '24h', label: 'Últimas 24h' },
  { value: '7d',  label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
];

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'agora mesmo';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `há ${d} dia${d > 1 ? 's' : ''}`;
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: '2-digit' });
}

export function Atividade() {
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [since, setSince] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Debounce 300ms — evita 1 request por keystroke enquanto Pablo digita.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery<Event[]>({
    queryKey: ['admin-activity-feed', typeFilter, since, debouncedSearch],
    queryFn: async () => {
      const params: any = { limit: 100 };
      if (typeFilter) params.types = typeFilter;
      if (since && since !== 'all') params.since = since;
      if (debouncedSearch) params.q = debouncedSearch;
      const { data } = await api.get('/admin/activity-feed', { params });
      return Array.isArray(data?.data) ? data.data : [];
    },
    refetchInterval: 30_000,
  });

  const items: Event[] = data ?? [];

  return (
    <Layout title="Atividade ao vivo">
      <p className="mb-4 text-sm text-text-muted">
        Feed unificado dos últimos eventos no app: cadastros, publicações, assinaturas Pro e denúncias.
        Atualiza sozinho a cada 30 s.
      </p>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          {TYPE_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>
        <Select value={since} onChange={(e) => setSince(e.target.value)}>
          {SINCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>
        <div className="relative min-w-[240px] flex-1">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            className="pl-9"
            placeholder="Buscar por usuário ou título…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        {isLoading && (
          <div className="p-8 text-center text-text-muted">Carregando…</div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="p-8 text-center text-text-muted">Sem eventos recentes.</div>
        )}
        <ul className="divide-y divide-border">
          {items.map((e) => {
            // ⚠️ O fallback NÃO mostra mais `e.type`. Imprimir o código cru onde
            // todo o resto tem frase faz o painel parecer quebrado, e ainda
            // esconde o problema real — que é tipo novo na API sem entrada
            // aqui. O código fica no `title`, para quem for investigar.
            const meta = TYPE_META[e.type] || {
              icon: Activity, label: 'Outro', verb: '', iconClass: 'text-text-muted',
            };
            const Icon = meta.icon;
            return (
              <li key={`${e.type}-${e.ref_id}-${e.at}`} className="flex items-start gap-3 px-4 py-3">
                <Icon size={18} className={`mt-0.5 shrink-0 ${meta.iconClass}`} />
                <div className="flex-1">
                  <div className="text-sm text-text-primary">
                    <span className="font-medium">{e.actor_name || e.actor_username}</span>{' '}
                    <span className="text-text-secondary">{meta.verb}</span>
                    {e.title && (
                      <>
                        {' '}
                        <span className="text-text-primary">{e.title}</span>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-text-muted">
                    @{e.actor_username} · {relativeTime(e.at)}
                  </div>
                </div>
                <span
                  className="rounded-md bg-bg-input px-2 py-0.5 text-xs text-text-muted"
                  title={e.type}
                >
                  {meta.label}
                </span>
              </li>
            );
          })}
        </ul>
      </Card>
    </Layout>
  );
}
